import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Languages, ArrowRight, Trophy, Users, MapPin, LineChart, Menu, X, CalendarDays, MessageCircle, Mail, Download } from "lucide-react";
import api from "@/api";
import { useLang, imgUrl } from "@/i18n";
import { useAuth } from "@/auth";

const HERO = "https://images.pexels.com/photos/38454829/pexels-photo-38454829.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_YOUTH = "https://images.unsplash.com/photo-1622659097972-68f1d8c1829f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHx5b3V0aCUyMGZvb3RiYWxsJTIwbWF0Y2glMjBhY3Rpb258ZW58MHx8fHwxNzg3MzgwMTQ2fDA&ixlib=rb-4.1.0&q=85";
const IMG_COACH = "https://images.pexels.com/photos/8941613/pexels-photo-8941613.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_TEAM = "https://images.pexels.com/photos/10347865/pexels-photo-10347865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { t, toggle, lang } = useLang();
  const { user } = useAuth();
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice.catch(() => null);
      setInstallPrompt(null);
    } else {
      window.alert(t("installHint") + " — " + (lang === "ms" ? "Menu pelayar → 'Add to Home Screen' / 'Tambah ke Skrin Utama'." : "Browser menu → 'Add to Home Screen'."));
    }
  };
  const [schedules, setSchedules] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [site, setSite] = useState(null);

  useEffect(() => {
    api.get("/public/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/news").then((r) => setNewsItems(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/site-info").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const sitePhone = site?.phone || "+60 12-345 6789";
  const siteEmail = site?.email || "info@glocalfootballacademy.com";
  const siteAddress = site?.address || t("homegroundAddress");
  const waHref = `https://wa.me/${sitePhone.replace(/\D/g, "")}`;
  const mailHref = `mailto:${siteEmail}`;
  const mapHref = `https://www.google.com/maps?q=${encodeURIComponent(siteAddress)}`;

  const trainings = schedules.filter((s) => ["training", "school"].includes(s.type));
  const matches = schedules.filter((s) => ["friendly", "match"].includes(s.type));

  const closeBurger = () => setBurgerOpen(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-white/20 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2.5">
            <img src="/logo.jpg" alt="Glocal Football Academy" className="h-10 w-10 rounded-lg object-cover ring-2 ring-[#84CC16]/60" data-testid="brand-logo" />
            <p className="font-['Outfit'] text-sm font-bold leading-tight text-[#165A2E] sm:text-base">Glocal Football<br className="hidden sm:block" /> Academy</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 md:flex">
            <button onClick={toggle} data-testid="landing-lang-toggle" className="flex items-center gap-1 rounded-full border border-[#165A2E]/30 px-3.5 py-1.5 text-xs font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white">
              <Languages size={13} /> {lang === "ms" ? "EN" : "MS"}
            </button>
            {user ? (
              <Link to="/app" data-testid="landing-dashboard-link" className="gfa-btn-primary text-sm">{t("dashboard")}</Link>
            ) : (
              <Link to="/login" data-testid="landing-login-link" className="gfa-btn-primary text-sm">{t("ctaLogin")}</Link>
            )}
            <button onClick={installApp} data-testid="install-app-btn" title={t("installApp")}
              className="flex items-center gap-1 rounded-full bg-[#84CC16] px-3.5 py-1.5 text-xs font-bold text-[#0A0A0A] transition-colors duration-200 hover:bg-[#65A30D]">
              <Download size={13} /> {t("installApp")}
            </button>
            </div>
            <button
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#165A2E] text-white transition-colors duration-200 hover:bg-[#114724]"
            onClick={() => setBurgerOpen(!burgerOpen)}
            data-testid="burger-menu-btn"
            aria-label="Menu"
          >
            {burgerOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {burgerOpen && (
          <nav className="absolute right-4 top-full z-50 mt-2 w-72 rounded-2xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-xl" data-testid="burger-menu-panel">
            <div className="flex flex-col gap-1 p-4">
              <a href="#home" onClick={closeBurger} data-testid="burger-home-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("home")}</a>
              <a href="#about" onClick={closeBurger} data-testid="burger-about-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("aboutUs")}</a>
              <a href="#program" onClick={closeBurger} data-testid="burger-program-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("programsTitle")}</a>
              <a href="#latihan" onClick={closeBurger} data-testid="burger-training-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("trainingSchedule")}</a>
              <a href="#perlawanan" onClick={closeBurger} data-testid="burger-match-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("matchSchedule")}</a>
              <a href="#news" onClick={closeBurger} data-testid="burger-news-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("news")}</a>
              <a href="#contact" onClick={closeBurger} data-testid="burger-contact-link" className="rounded-lg px-4 py-3 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">{t("contactUs")}</a>
              <button onClick={() => { toggle(); closeBurger(); }} data-testid="burger-lang-toggle" className="flex items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">
                <Languages size={15} /> {t("langName")}
              </button>
              <div className="mt-2 flex flex-col gap-2 border-t border-slate-100 pt-3">
                <button onClick={() => { installApp(); closeBurger(); }} data-testid="burger-install-btn"
                  className="flex items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-bold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E]/5">
                  <Download size={15} /> {t("installApp")}
                </button>
                {user ? (
                  <Link to="/app" onClick={closeBurger} data-testid="burger-dashboard-link" className="gfa-btn-primary text-center text-sm">{t("dashboard")}</Link>
                ) : (
                  <>
                    <Link to="/register" onClick={closeBurger} data-testid="burger-register-link" className="gfa-btn-accent text-center text-sm">{t("ctaRegister")}</Link>
                    <Link to="/login" onClick={closeBurger} data-testid="burger-login-link" className="rounded-full border border-[#165A2E]/30 px-6 py-2.5 text-center text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white">{t("ctaLogin")}</Link>
                  </>
                )}
              </div>
            </div>
          </nav>
        )}
      </header>

      <section id="home" className="grain-overlay relative flex min-h-[88vh] items-center overflow-hidden">
        <img src={HERO} alt="GFA" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#165A2E]/95 via-[#165A2E]/70 to-[#165A2E]/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-32">
          <p className="animate-fade-up mb-4 inline-block rounded-full bg-[#84CC16] px-4 py-1 text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">U6 — U14</p>
          <h1 className="animate-fade-up stagger-1 max-w-2xl text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {t("heroTitle")}
          </h1>
          <p className="animate-fade-up stagger-2 mt-6 max-w-xl text-base text-white/85 md:text-lg">{t("heroSub")}</p>
          <div className="animate-fade-up stagger-3 mt-10 flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-register-btn" className="gfa-btn-accent inline-flex items-center gap-2">
              {t("ctaRegister")} <ArrowRight size={16} />
            </Link>
            <Link to="/login" data-testid="hero-login-btn" className="rounded-full border border-white/50 px-6 py-2.5 font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-[#165A2E]">
              {t("ctaLogin")}
            </Link>
          </div>
        </div>
      </section>

      <section id="about" className="bg-[#F8FAFC] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-['Outfit'] text-3xl font-bold text-[#165A2E] md:text-4xl" data-testid="about-title">{t("aboutUs")}</h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">{t("aboutText")}</p>
        </div>
      </section>

      <section id="jadual" className="bg-[#0d3a1e] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-['Outfit'] text-3xl font-bold text-white md:text-4xl" data-testid="home-sched-title">{t("homeSchedTitle")}</h2>
          <p className="mt-2 text-sm text-white/60">{t("homeSchedSub")}</p>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            {[{ list: trainings, label: t("training"), key: "training", anchor: "latihan" }, { list: matches, label: t("matchesPlayed"), key: "match", anchor: "perlawanan" }].map((col) => (
              <div key={col.key} id={col.anchor} className="scroll-mt-20 rounded-xl bg-[#114724] p-6">
                <h3 className="mb-4 flex items-center gap-2 font-['Outfit'] text-lg font-bold text-[#84CC16]">
                  <CalendarDays size={18} /> {col.label}
                </h3>
                <div className="space-y-3">
                  {col.list.slice(0, 4).map((s) => (
                    <div key={s.schedule_id} className="rounded-lg bg-white/5 p-4 transition-colors duration-200 hover:bg-white/10" data-testid={`home-sched-${s.schedule_id}`}>
                      <p className="font-semibold text-white">{s.title}</p>
                      <p className="mt-1 text-xs text-white/60">
                        {s.date} · {s.start_time} - {s.end_time}{s.category ? ` · ${s.category}` : ""}
                      </p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-white/50"><MapPin size={11} /> {s.location_name}</p>
                    </div>
                  ))}
                  {col.list.length === 0 && <p className="text-sm text-white/40">{t("noData")}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="program" className="bg-[#0A0A0A] py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-12 font-['Outfit'] text-3xl font-bold text-white md:text-4xl" data-testid="why-title">{t("whyTitle")}</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            <div className="gfa-card-hover relative overflow-hidden rounded-xl bg-[#114724] md:col-span-8">
              <img src={IMG_YOUTH} alt="" className="h-64 w-full object-cover opacity-80" />
              <div className="p-8">
                <Users className="mb-3 text-[#84CC16]" size={28} />
                <h3 className="font-['Outfit'] text-xl font-bold text-white">{t("featAge")}</h3>
                <p className="mt-2 text-sm text-white/70">{t("featAgeSub")}</p>
              </div>
            </div>
            <div className="gfa-card-hover rounded-xl bg-[#114724] p-8 md:col-span-4">
              <Trophy className="mb-3 text-[#84CC16]" size={28} />
              <h3 className="font-['Outfit'] text-xl font-bold text-white">{t("featCoaching")}</h3>
              <p className="mt-2 text-sm text-white/70">{t("featCoachingSub")}</p>
              <img src={IMG_COACH} alt="" className="mt-6 h-40 w-full rounded-lg object-cover" />
            </div>
            <div className="gfa-card-hover rounded-xl bg-[#114724] p-8 md:col-span-4">
              <MapPin className="mb-3 text-[#84CC16]" size={28} />
              <h3 className="font-['Outfit'] text-xl font-bold text-white">{t("featFacility")}</h3>
              <p className="mt-2 text-sm text-white/70">{t("featFacilitySub")}</p>
            </div>
            <div className="gfa-card-hover relative overflow-hidden rounded-xl bg-[#114724] md:col-span-8">
              <div className="flex flex-col md:flex-row">
                <div className="p-8 md:w-1/2">
                  <LineChart className="mb-3 text-[#84CC16]" size={28} />
                  <h3 className="font-['Outfit'] text-xl font-bold text-white">{t("featTrack")}</h3>
                  <p className="mt-2 text-sm text-white/70">{t("featTrackSub")}</p>
                </div>
                <img src={IMG_TEAM} alt="" className="h-48 w-full object-cover md:h-auto md:w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="news" className="scroll-mt-20 bg-[#F8FAFC] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-['Outfit'] text-3xl font-bold text-[#165A2E] md:text-4xl" data-testid="home-news-title">{t("latestNews")}</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {newsItems.map((n) => (
              <article key={n.news_id} className="gfa-card gfa-card-hover overflow-hidden" data-testid={`home-news-${n.news_id}`}>
                {n.image_path && <img src={imgUrl(n.image_path)} alt="" className="h-40 w-full object-cover" />}
                <div className="p-5">
                  <h3 className="font-['Outfit'] font-bold text-slate-800">{n.title}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-slate-600">{n.content}</p>
                  <p className="mt-3 text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString()}</p>
                </div>
              </article>
            ))}
            {newsItems.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
          </div>
        </div>
      </section>

      <section id="contact" className="scroll-mt-20 bg-[#114724] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-['Outfit'] text-3xl font-bold text-white md:text-4xl" data-testid="contact-title">{t("contactUs")}</h2>
          <p className="mt-4 max-w-xl text-white/70">{t("contactText")}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-2.5 font-semibold text-white transition-colors duration-200 hover:bg-[#1da851]">
              <MessageCircle size={16} /> {t("whatsappUs")}
            </a>
            <a href={mailHref} data-testid="contact-email"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-2.5 font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-[#165A2E]">
              <Mail size={16} /> {t("emailUs")}
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-[#0d3a1e] py-14" data-testid="landing-footer">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-2.5">
            <img src="/logo.jpg" alt="Glocal Football Academy" className="h-9 w-9 rounded-lg object-cover ring-2 ring-[#84CC16]/60" />
              <p className="font-['Outfit'] text-lg font-bold text-white" data-testid="footer-brand">Glocal Football Academy</p>
            </div>
            <p className="mt-3 text-sm text-white/60">{site ? (lang === "ms" ? site.tagline_ms : site.tagline_en) : t("heroSub").split(".")[0] + "."}</p>
          </div>
          <div>
            <p className="font-['Outfit'] font-bold text-[#84CC16]" data-testid="footer-contact-title">{t("contactUs")}</p>
            <div className="mt-4 space-y-2 text-sm text-white/70">
              <a href={waHref} target="_blank" rel="noreferrer" className="flex items-center gap-2 transition-colors duration-200 hover:text-[#84CC16]" data-testid="footer-whatsapp">
                <MessageCircle size={14} /> {sitePhone}
              </a>
              <a href={mailHref} className="flex items-center gap-2 transition-colors duration-200 hover:text-[#84CC16]" data-testid="footer-email">
                <Mail size={14} /> {siteEmail}
              </a>
            </div>
          </div>
          <div>
            <p className="font-['Outfit'] font-bold text-[#84CC16]" data-testid="footer-homeground-title">{t("homeground")}</p>
            <p className="mt-4 flex items-start gap-2 text-sm text-white/70">
              <MapPin size={14} className="mt-0.5 shrink-0" /> {siteAddress}
            </p>
            <a href={mapHref} target="_blank" rel="noreferrer" data-testid="footer-map-link"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-1.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#84CC16] hover:text-[#0A0A0A] hover:border-[#84CC16]">
              <MapPin size={12} /> {t("viewOnMap")}
            </a>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-7xl border-t border-white/10 px-6 pt-6">
          <p className="text-sm text-white/50">© 2026 Glocal Football Academy. {lang === "ms" ? "Hak cipta terpelihara." : "All rights reserved."}</p>
        </div>
      </footer>
    </div>
  );
}
